import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useUserData } from './UserDataContext';
import { Coins, Ticket, Tag, Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import goldIcon from '../assets/gold icon.png';
import { toast } from 'sonner';
import { TAGS, INTEREST_TAGS } from '../constants/TagDictionary';
import { TribeService } from '../services/api';
import { ImageCropperModal } from './ImageCropperModal';

const TRIBE_TAGS = INTEREST_TAGS;

interface CreateTribeModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function CreateTribeModal({ open, onOpenChange }: CreateTribeModalProps) {
    const { userProfile, createTribe } = useUserData();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [tags, setTags] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState<'credit' | 'gold'>('credit');
    const [bannerFile, setBannerFile] = useState<File | null>(null);
    const [bannerPreview, setBannerPreview] = useState<string | null>(null);
    const [cropModalOpen, setCropModalOpen] = useState(false);
    const [cropImage, setCropImage] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [agreed, setAgreed] = useState(false);

    // Reset agreement each time the modal opens so the user always sees it.
    React.useEffect(() => { if (!open) setAgreed(false); }, [open]);

    // Caps
    const ownedCount = (userProfile.ownedTribeIds || []).length;
    const maxTribes = 4;
    const isCapReached = ownedCount >= maxTribes;

    // Costs
    const credits = userProfile.tribeCreationCredits || 0;
    const hasCredits = credits > 0;
    const goldCost = 1000;
    const userGold = userProfile.goldBalance || 0;
    const hasGold = userGold >= goldCost;

    const canCreate = !isCapReached && (hasCredits || hasGold);

    // Set default payment method based on availability
    React.useEffect(() => {
        if (hasCredits) {
            setPaymentMethod('credit');
        } else if (hasGold) {
            setPaymentMethod('gold');
        }
    }, [hasCredits, hasGold]);

    const handleBannerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                toast.error("Image size must be less than 5MB");
                return;
            }
            
            const reader = new FileReader();
            reader.onloadend = () => {
                setCropImage(reader.result as string);
                setCropModalOpen(true);
            };
            reader.readAsDataURL(file);
            e.target.value = '';
        }
    };

    const dataURLToBlob = (dataURL: string) => {
        const arr = dataURL.split(',');
        const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new Blob([u8arr], { type: mime });
    };

    const handleCropSave = (croppedImageUrl: string) => {
        setBannerPreview(croppedImageUrl);
        const blob = dataURLToBlob(croppedImageUrl);
        const file = new File([blob], "tribe-banner.jpg", { type: "image/jpeg" });
        setBannerFile(file);
        setCropModalOpen(false);
    };

    const handleSubmit = async () => {
        if (!name.trim() || !description.trim()) {
            toast.error("Name and description are required");
            return;
        }

        setLoading(true);
        try {
            let bannerUrl = ''; // Default to empty to use gradient placeholder

            if (bannerFile && userProfile.id) {
                try {
                    bannerUrl = await TribeService.uploadCommunityBanner(String(userProfile.id), 'tribe', bannerFile);
                } catch (uploadError) {
                    console.error("Banner upload failed, using default:", uploadError);
                    toast.error("Banner upload failed, using default image");
                }
            }

            const tribeId = await createTribe({
                name,
                description,
                tags,
                bannerUrl: bannerUrl,
                bannerImage: bannerUrl
            }, paymentMethod);
            onOpenChange(false);

            // Navigate to new tribe
            const newTribe = {
                id: tribeId,
                name: name,
                description: description,
                image: bannerUrl,
                members: 1,
                ownerId: userProfile?.id,
                isNew: true
            };

            // Refresh communities list
            window.dispatchEvent(new CustomEvent('REFRESH_COMMUNITIES'));

            setTimeout(() => {
                window.dispatchEvent(new CustomEvent('navigateToTribeGroup', { detail: newTribe }));
            }, 500);

            // Reset form
            setName('');
            setDescription('');
            setTags([]);
            setBannerFile(null);
            setBannerPreview(null);
        } catch (error: any) {
            console.error(error);
            toast.error(error.message || "Failed to establish tribe");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            {/* Same width fix as CreateForumModal, and for the same reason: the
                w-[min(480px,92vw)] class that was here has no rule in the
                committed pre-compiled stylesheet, so the dialog had no width and
                shrank to fit its content. Kept identical to Create Forum so the
                two sibling modals match. */}
            <DialogContent
                style={{ width: 'min(560px, 92vw)' }}
                className="max-w-none bg-[#FDFBF7] dark:bg-[#1a1a1a] border-black/5 dark:border-white/10 flex flex-col h-[80vh] max-h-[80vh] p-0 overflow-hidden shadow-2xl gap-0"
            >

                {/* ── Step 0: Agreement screen ───────────────────────────────── */}
                {!agreed && (
                    <div className="flex flex-col h-full p-6 gap-5 overflow-y-auto">
                        <div>
                            <h2 className="text-2xl font-bold text-black dark:text-white mb-1">What is a Tribe?</h2>
                            <p className="text-sm text-black/50 dark:text-white/50">Please read before creating one.</p>
                        </div>

                        <div className="flex flex-col gap-4 text-sm text-black/80 dark:text-white/80 leading-relaxed">
                            <p>
                                A <strong>Tribe</strong> is a community of like-minded people united by a shared interest — a dedicated space to discuss topics and have open dialogue.
                            </p>
                            <p>
                                Think: anime watchers, Afrobeats fans, food enthusiasts, fitness communities, gaming clans, tech lovers, and more.
                            </p>
                            <div className="rounded-xl p-4 border border-amber-400/30 bg-amber-50 dark:bg-amber-950/30">
                                <p className="font-semibold text-amber-700 dark:text-amber-400 mb-1">Important</p>
                                <p className="text-amber-800 dark:text-amber-300">
                                    Tribes are <strong>not</strong> connected to real ethnic tribes, cultural groups, or nationalities. The word "Tribe" here simply means a collection of people who share a passion — nothing more.
                                </p>
                            </div>
                        </div>

                        <div className="mt-auto">
                            <Button
                                className="w-full bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] text-white font-bold rounded-xl h-12"
                                onClick={() => setAgreed(true)}
                            >
                                I understand — create a Tribe
                            </Button>
                        </div>
                    </div>
                )}

                {/* ── Step 1+: Creation form ─────────────────────────────────── */}
                {agreed && <>
                <DialogHeader className="p-6 pb-4 border-b border-black/10 dark:border-white/10 shrink-0">
                    <DialogTitle className="text-xl font-bold flex items-center justify-between font-outfit">
                        <span>Establish Tribe</span>
                        <div className="text-xs font-normal px-2 py-1 rounded bg-black/5 dark:bg-white/10 text-black/60 dark:text-white/60">
                            Owned: {ownedCount}/{maxTribes}
                        </div>
                    </DialogTitle>
                    <DialogDescription className="text-sm text-black/60 dark:text-white/60 font-outfit">
                        Tribes are private or semi-private communities. Connect with people based on favorite interests, schools, occupations etc. These are not related to real Tribes at all.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto px-6 custom-scrollbar min-h-0">
                    <div className="py-6 space-y-6">
                        {/* Banner Upload Section */}
                        <div className="space-y-2">
                            <Label className="flex justify-between items-center font-outfit">
                                <span className="font-bold text-base">Tribe Banner</span>
                                <span className="text-[10px] text-black/40 dark:text-white/40 uppercase font-bold tracking-wider">Recommended: 1200x300</span>
                            </Label>
                            <input
                                type="file"
                                ref={fileInputRef}
                                className="hidden"
                                accept="image/*"
                                onChange={handleBannerChange}
                            />
                            <div 
                                onClick={() => fileInputRef.current?.click()}
                                className="relative group cursor-pointer"
                            >
                                <div className={`w-full h-32 rounded-2xl overflow-hidden border-2 border-dashed transition-all flex items-center justify-center bg-black/5 dark:bg-white/5 ${bannerPreview ? 'border-amber-500/50 dark:border-blue-500/50' : 'border-black/10 dark:border-white/10 hover:border-amber-500/30'}`}>
                                    {bannerPreview ? (
                                        <>
                                            <img src={bannerPreview} className="w-full h-full object-cover shadow-inner" alt="Banner preview" />
                                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                <div className="flex flex-col items-center gap-2">
                                                    <Upload className="w-10 h-10 text-white" />
                                                    <span className="text-white text-xs font-bold font-outfit">Change Image</span>
                                                </div>
                                            </div>
                                            <button 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setBannerFile(null);
                                                    setBannerPreview(null);
                                                }}
                                                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 backdrop-blur-md flex items-center justify-center text-white hover:bg-red-500 transition-all transform hover:scale-110 shadow-lg"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </>
                                    ) : (
                                        <div className="flex flex-col items-center gap-3 text-black/40 dark:text-white/40 group-hover:text-amber-500/60 transition-colors font-outfit">
                                            <div className="p-4 rounded-full bg-black/5 dark:bg-white/5 shadow-sm">
                                                <ImageIcon className="w-10 h-10" />
                                            </div>
                                            <div className="text-center">
                                                <p className="text-base font-bold">Upload Custom Banner</p>
                                                <p className="text-xs">Tap to browse files</p>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {loading && bannerFile && (
                                        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-10 rounded-xl">
                                            <Loader2 className="w-10 h-10 text-white animate-spin" />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label className="font-bold flex items-center gap-2">
                                <Tag className="w-4 h-4 text-[#F39C12] dark:text-[#4478ea]" />
                                Tribe Name
                            </Label>
                            <Input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="e.g. Afrobeats Lovers"
                                maxLength={30}
                                className="bg-zinc-100/50 dark:bg-black/40 border-black/10 dark:border-white/10 h-12 text-base font-medium rounded-xl"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="font-bold">Description</Label>
                            <Textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Who is this tribe for?"
                                maxLength={150}
                                className="bg-zinc-100/50 dark:bg-black/40 border-black/10 dark:border-white/10 min-h-[100px] text-base font-medium rounded-xl p-4"
                            />
                        </div>

                        <div className="space-y-3">
                            <Label className="font-bold flex items-center justify-between">
                                <span>Tags (Max 5)</span>
                                <span className="text-[10px] text-[#F39C12] dark:text-[#4478ea] font-black uppercase">{tags.length}/5 Selected</span>
                            </Label>
                            <div className="flex flex-wrap gap-2 p-3 rounded-2xl bg-zinc-100/50 dark:bg-black/40 border border-black/10 dark:border-white/10">
                                {TRIBE_TAGS.map((tag) => (
                                    <button
                                        key={tag}
                                        type="button"
                                        onClick={() => {
                                            if (tags.includes(tag)) {
                                                setTags(tags.filter(t => t !== tag));
                                            } else {
                                                if (tags.length >= 5) {
                                                    toast.error("Max 5 tags allowed");
                                                    return;
                                                }
                                                setTags([...tags, tag]);
                                            }
                                        }}
                                        className={`px-4 py-2 text-xs font-bold rounded-full border transition-all duration-300 ${tags.includes(tag)
                                            ? 'bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#3498DB] text-white border-transparent shadow-md transform scale-105'
                                            : 'bg-white dark:bg-[#1a1a1a] text-black/60 dark:text-white/60 border-black/10 dark:border-white/10 hover:border-[#F39C12] dark:hover:border-[#4478ea]'
                                            }`}
                                    >
                                        {tag}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Cost Selection Loop */}
                        <div className="space-y-4">
                            {isCapReached ? (
                                <div className="p-4 rounded-2xl bg-linear-to-r from-red-50 to-orange-50 dark:from-red-900/10 dark:to-orange-900/10 border border-red-200/50 dark:border-red-800/30 flex items-center gap-3">
                                    <div className="p-2 rounded-full bg-white dark:bg-black/40 shadow-sm shrink-0">
                                        <X className="w-5 h-5 text-red-500" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-red-500 uppercase tracking-tight leading-none">Tribe Cap Reached</p>
                                        <p className="text-xs text-red-400 font-medium mt-1">You have reached the maximum Tribe limit (4).</p>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <Label className="font-bold flex items-center gap-2">
                                        <Coins className="w-4 h-4 text-[#F39C12] dark:text-[#4478ea]" />
                                        Payment Method
                                    </Label>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {/* Link Credit Option */}
                                        <button
                                            type="button"
                                            onClick={() => hasCredits && setPaymentMethod('credit')}
                                            disabled={!hasCredits}
                                            className={`relative p-4 rounded-2xl border-2 transition-all text-left flex flex-col gap-1 shadow-sm ${
                                                paymentMethod === 'credit'
                                                    ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/10 scale-[1.02]'
                                                    : 'border-black/5 bg-zinc-50 dark:bg-white/5 opacity-80 hover:opacity-100'
                                            } ${!hasCredits ? 'opacity-40 cursor-not-allowed grayscale' : ''}`}
                                        >
                                            <div className="flex items-center gap-2 font-black text-sm uppercase tracking-wider">
                                                <Ticket className={`w-4 h-4 ${paymentMethod === 'credit' ? 'text-emerald-600' : 'text-zinc-500'}`} />
                                                <span>Use Credit</span>
                                            </div>
                                            <span className="text-xs font-bold text-black/40 dark:text-white/40">
                                                {credits} Available
                                            </span>
                                            {paymentMethod === 'credit' && (
                                                <div className="absolute top-3 right-3 w-3 h-3 rounded-full bg-emerald-500 shadow-sm" />
                                            )}
                                        </button>

                                        {/* Gold Option */}
                                        <button
                                            type="button"
                                            onClick={() => hasGold && setPaymentMethod('gold')}
                                            disabled={!hasGold}
                                            className={`relative p-4 rounded-2xl border-2 transition-all text-left flex flex-col gap-1 shadow-sm ${
                                                paymentMethod === 'gold'
                                                    ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/10 scale-[1.02]'
                                                    : 'border-black/5 bg-zinc-50 dark:bg-white/5 opacity-80 hover:opacity-100'
                                            } ${!hasGold ? 'opacity-40 cursor-not-allowed' : ''}`}
                                        >
                                            <div className="flex items-center gap-2 font-black text-sm uppercase tracking-wider">
                                                <img src={goldIcon as any} alt="Gold" className="h-4 w-auto object-contain" />
                                                <span>Use Gold</span>
                                            </div>
                                            <div className="flex flex-col">
                                                <span className="text-xs font-bold text-amber-600 dark:text-blue-400">
                                                    Cost: {goldCost}
                                                </span>
                                                <span className="text-[10px] text-black/40 dark:text-white/40">
                                                    Balance: {userGold}
                                                </span>
                                            </div>
                                            {paymentMethod === 'gold' && (
                                                <div className="absolute top-3 right-3 w-3 h-3 rounded-full bg-amber-500 shadow-sm" />
                                            )}
                                        </button>
                                    </div>
                                    {!hasCredits && !hasGold && (
                                         <p className="text-xs text-red-500 font-bold text-center bg-red-50 dark:bg-red-900/10 p-2 rounded-lg border border-red-200/50">
                                            ⚠️ Insufficient credits and gold to establish a tribe.
                                        </p>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <DialogFooter className="p-6 pt-4 border-t border-black/10 dark:border-white/10 shrink-0 pb-8 sm:pb-6">
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={loading || !canCreate}
                        className="w-full bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#3498DB] hover:opacity-90 text-white py-6 rounded-xl text-lg font-bold shadow-lg shadow-orange-500/20 dark:shadow-blue-500/20"
                    >
                        {loading ? 'Establishing...' : 'Establish Tribe'}
                    </Button>
                </DialogFooter>
                </>}
            </DialogContent>

            <ImageCropperModal
                isOpen={cropModalOpen}
                onClose={() => setCropModalOpen(false)}
                imageUrl={cropImage || ''}
                onSave={handleCropSave}
                aspectRatio={4}
                outputWidth={1600}
                title="Adjust Tribe Banner"
                description="Fit the image into the 1200x300 frame"
                circular={false}
                onUploadNew={() => fileInputRef.current?.click()}
            />
        </Dialog>
    );
}
